import { Box, HStack, Select, Text, VStack } from 'native-base'

import { CaretDown, Moon } from '@phosphor-icons/react'

import { DayHours, DayOfWeek } from '@consumer/graphql'
import { colors } from '../../utils/theme/configureTheme'
import CustomSwitch from './CustomSwitch'
import TimeWindowError from './TimeWindowError'

interface DayHoursRowProps {
  dayHours: DayHours
  onToggle: (isEnabled: boolean) => void
  onOpenTimeChange: (time: string) => void
  onCloseTimeChange: (time: string) => void
  // Available time options for the time select component in 12 hour format (e.g., "8:00am", "5:00pm")
  timeOptions: string[]
}

// Helper to get display name for day of week
const getDayDisplayName = (day: DayOfWeek): string => {
  const dayMap: Record<DayOfWeek, string> = {
    [DayOfWeek.Monday]: 'Mon',
    [DayOfWeek.Tuesday]: 'Tue',
    [DayOfWeek.Wednesday]: 'Wed',
    [DayOfWeek.Thursday]: 'Thu',
    [DayOfWeek.Friday]: 'Fri',
    [DayOfWeek.Saturday]: 'Sat',
    [DayOfWeek.Sunday]: 'Sun',
  }
  return dayMap[day]
}

// Convert 24-hour format to 12-hour format (e.g., "0800" -> "8:00am")
const format24To12Hour = (time24: string): string => {
  const hours = parseInt(time24.slice(0, 2), 10)
  const h = hours % 12 === 0 ? 12 : hours % 12
  const ampm = hours < 12 ? 'am' : 'pm'
  const m = time24.slice(2)
  return `${h}:${m}${ampm}`
}

// Calculate if time window is less than 6 hours
const isTimeWindowTooSmall = (openTime: string | null | undefined, closeTime: string | null | undefined): boolean => {
  if (!openTime || !closeTime) return false

  const openHours = parseInt(openTime.slice(0, 2), 10)
  const openMinutes = parseInt(openTime.slice(2), 10)
  const closeHours = parseInt(closeTime.slice(0, 2), 10)
  const closeMinutes = parseInt(closeTime.slice(2), 10)

  const openTotalMinutes = openHours * 60 + openMinutes
  let closeTotalMinutes = closeHours * 60 + closeMinutes

  // Handle wraparound to next day (e.g., 7pm to 12am)
  if (closeTotalMinutes <= openTotalMinutes) {
    closeTotalMinutes += 24 * 60 // Add 24 hours in minutes
  }

  const differenceMinutes = closeTotalMinutes - openTotalMinutes
  const differenceHours = differenceMinutes / 60

  return differenceHours < 6
}

// Time select component with Figma design specs
const TimeSelect = ({
  value,
  onChange,
  timeOptions,
}: {
  value: string
  onChange: (time: string) => void
  timeOptions: string[]
}) => (
  <Select
    width="106px"
    selectedValue={value}
    onValueChange={onChange}
    fontSize="sm"
    fontWeight="500"
    lineHeight="125%"
    letterSpacing="-1%"
    color={colors.brand.softBlack}
    height="48px"
    minHeight="48px"
    borderColor={colors.brand.darkGrey}
    borderWidth={2}
    borderRadius={4}
    py="16px"
    px="8px"
    _selectedItem={{
      bg: 'blue.100',
    }}
    _actionSheetContent={{
      maxHeight: '192px',
    }}
    dropdownIcon={<CaretDown weight="bold" color={colors.brand.softBlack} style={{ marginRight: '8px' }} />}
  >
    {timeOptions.map(time => (
      <Select.Item key={time} label={time} value={time} />
    ))}
  </Select>
)

/** Row component for displaying and editing the open/close hours for a single day of the week. */
export default function DayHoursRow({
  dayHours,
  onToggle,
  onOpenTimeChange,
  onCloseTimeChange,
  timeOptions,
}: DayHoursRowProps) {
  const isOpen = !dayHours.isClosed
  const openTime24hr = dayHours.openTime ?? '0800'
  const closeTime24hr = dayHours.closeTime ?? '1700'
  const showTimeWindowError = isOpen && isTimeWindowTooSmall(openTime24hr, closeTime24hr)
  const openTime12hr = format24To12Hour(openTime24hr)
  const closeTime12hr = format24To12Hour(closeTime24hr)

  return (
    <VStack space={2} width="100%">
      <HStack alignItems="center" space={1}>
        {/* Day toggle */}
        <CustomSwitch
          isChecked={isOpen}
          onToggle={onToggle}
          offTrackColor="#5D5D5A"
          onTrackColor={colors.brand.selectedBlue}
        />

        {/* Day name */}
        <Text width="36px" fontSize="sm" color={colors.brand.softBlack}>
          {getDayDisplayName(dayHours.dayOfWeek)}
        </Text>

        <div className={isOpen ? 'day-hours-open' : 'day-hours-closed'} key={isOpen ? 'open' : 'closed'}>
          {isOpen ? (
            <HStack alignItems="center" space={1}>
              {/* Open time */}
              <TimeSelect value={openTime12hr} onChange={onOpenTimeChange} timeOptions={timeOptions} />

              <Text fontSize="sm" fontWeight="400" color={colors.brand.softBlack} minWidth="20px" textAlign="center">
                To
              </Text>

              {/* Close time */}
              <TimeSelect value={closeTime12hr} onChange={onCloseTimeChange} timeOptions={timeOptions} />
            </HStack>
          ) : (
            <Box
              minWidth="240px"
              height="48px"
              bg={colors.brand.lightGrey}
              borderRadius={4}
              borderWidth={2}
              borderColor="gray.300"
              py="16px"
              px="8px"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <HStack alignItems="center" space={1}>
                <Moon size={24} color={colors.brand.softBlack} />

                <Text fontSize="sm" fontWeight="500" color={colors.brand.softBlack}>
                  Closed
                </Text>
              </HStack>
            </Box>
          )}
        </div>
      </HStack>

      {/* Time window error */}
      {showTimeWindowError && <TimeWindowError />}
    </VStack>
  )
}
